/**
 * MethodScript Syntax Definitions
 *
 * This file provides utility functions for working with MethodScript syntax.
 * The actual keyword and command lists are auto-generated from command-parameters.json.
 *
 * IMPORTANT: Do not manually edit keyword/command lists here.
 * They are generated from PalmSens.MethodScript/Grammar/command-parameters.json
 * Run `npm run generate` to regenerate the syntax data.
 */

// Import generated syntax data
export { controlKeywords, measurementCommands, standardCommands, operators, allCommands, allKeywords } from './generated/syntax-data.js';

/**
 * Names of optional arguments used in measurement loop commands.
 *
 * These appear after the required parameters as `name(...)` blocks,
 * e.g. `meas_loop_ca pot curr 100m 1 1 add_meas(ab) nscans(3)`.
 *
 * This single list is the source of truth used by both the variable tracker
 * (to stop parsing before optional args) and the completion context detector
 * (to provide completions inside optional arg parentheses).
 */
export const OPTIONAL_ARGUMENT_NAMES: ReadonlyArray<string> = [
    'add_meas',
    'eis_acdc',
    'eis_dual_acdc',
    'eis_dual_tdd',
    'eis_opt',
    'eis_tdd',
    'ms_eis_acdc',
    'nscans',
    'nscans_avg',
    'nscans_equil',
    'poly_we',
    'time',
    'window',
] as const;

/**
 * Build a regex that matches any optional argument name followed by `(`.
 *
 * The returned pattern uses a word-boundary anchor (`\b`) so it won't match
 * substrings of longer identifiers.
 *
 * @param trailingPattern - Characters expected after the name.
 *   Defaults to `\\s*\\(` (optional whitespace then open paren).
 */
export function buildOptionalArgRegex(trailingPattern = '\\s*\\('): RegExp {
    return new RegExp(`\\b(${OPTIONAL_ARGUMENT_NAMES.join('|')})${trailingPattern}`);
}

/**
 * Get regex pattern for a list of keywords
 */
export function getKeywordPattern(keywords: ReadonlyArray<string>): string {
    if (keywords.length === 1) {
        return `\\b${keywords[0]}\\b`;
    }
    return `\\b(${keywords.join('|')})\\b`;
}

/**
 * Split keywords into chunks for better regex readability
 * Max 6 keywords per chunk
 */
export function chunkKeywords(keywords: ReadonlyArray<string>, chunkSize = 6): Array<Array<string>> {
    const result: Array<Array<string>> = [];
    for (let i = 0; i < keywords.length; i += chunkSize) {
        result.push([...keywords.slice(i, i + chunkSize)]);
    }
    return result;
}
